import { oc } from '@orpc/contract';
import * as z from 'zod';
import { devicePresenceSchema } from '../domain/device-activity.js';
import { contentTypeSchema } from '../enums/content.js';
import { playbackGateReasonSchema } from '../enums/playback-gate.js';
import { contentItemIdSchema, viewingSessionIdSchema } from '../primitives/id.js';
import { durationSecondsSchema, instantSchema } from '../primitives/time.js';
import { API_VERSION } from './version.js';
const base = `/${API_VERSION}/sessions`;
/**
 * The answer to "may this keep playing, and for how long".
 *
 * Every field comes from `@kidshub/policy`; nothing here is recomputed. The
 * device enforces the same decision offline using the same functions, so a
 * disagreement between this response and the tablet would mean the two had
 * drifted — which is the failure the shared package exists to prevent.
 */
export const gateDecisionSchema = z.object({
    isAllowed: z.boolean(),
    reason: playbackGateReasonSchema,
    remainingSeconds: durationSecondsSchema,
    limitingFactor: z.enum(['NONE', 'DAILY_TOTAL', 'CONTENT_TYPE', 'SESSION', 'SCHEDULE']),
});
export const sessionsContract = {
    /**
     * Opens a session, or refuses to.
     *
     * The gate is consulted here rather than after the fact: a session that
     * should never have started has already put a child in front of a screen.
     */
    start: oc
        .route({ method: 'POST', path: `${base}/start` })
        .input(z.object({
        contentType: contentTypeSchema,
        contentItemId: contentItemIdSchema.optional(),
    }))
        .output(z.discriminatedUnion('status', [
        z.object({
            status: z.literal('started'),
            sessionId: viewingSessionIdSchema,
            decision: gateDecisionSchema,
        }),
        z.object({ status: z.literal('refused'), decision: gateDecisionSchema }),
    ]))
        .errors({ DEVICE_UNPAIRED: { status: 403, message: 'This device is no longer paired' } }),
    /**
     * Reports progress and asks whether to continue.
     *
     * `elapsedMs` is measured against a monotonic clock. The wall-clock span is
     * sent alongside it so the server can notice a device whose date has moved:
     * the two should advance together, and a divergence is resolved in the
     * budget's favour rather than the child's (SPEC.md §19.2).
     */
    heartbeat: oc
        .route({ method: 'POST', path: `${base}/{sessionId}/heartbeat` })
        .input(z.object({
        sessionId: viewingSessionIdSchema,
        elapsedMs: z.int().nonnegative(),
        wallClockElapsedMs: z.int().nonnegative(),
    }))
        .output(z.object({
        decision: gateDecisionSchema,
        /** True when the device's clock disagrees with elapsed time. */
        clockSkewDetected: z.boolean(),
        serverTime: instantSchema,
    }))
        .errors({ SESSION_NOT_FOUND: { status: 404, message: 'Session not found or already ended' } }),
    end: oc
        .route({ method: 'POST', path: `${base}/{sessionId}/end` })
        .input(z.object({
        sessionId: viewingSessionIdSchema,
        elapsedMs: z.int().nonnegative(),
    }))
        .output(z.object({ success: z.literal(true) }))
        .errors({ SESSION_NOT_FOUND: { status: 404, message: 'Session not found or already ended' } }),
};
/** What a parent sees on the dashboard. */
export const liveDeviceStateSchema = z.object({
    isOnline: z.boolean(),
    /** What the tablet is doing, in one word. The dashboard's headline. */
    presence: devicePresenceSchema,
    lastSeenAt: instantSchema.nullable(),
    activeSession: z
        .object({
        sessionId: viewingSessionIdSchema,
        contentType: contentTypeSchema,
        contentItemId: contentItemIdSchema.nullable(),
        startedAt: instantSchema,
        elapsedSeconds: durationSecondsSchema,
    })
        .nullable(),
    remainingSeconds: durationSecondsSchema,
    /** Which limit `remainingSeconds` comes from, as the gate saw it. */
    limitingFactor: gateDecisionSchema.shape.limitingFactor,
    reason: playbackGateReasonSchema,
    spentTodaySeconds: durationSecondsSchema,
});
//# sourceMappingURL=sessions.js.map